import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users,
  FolderKanban,
  CheckCircle2,
  MessageSquare,
  TrendingUp,
  ArrowUpRight,
  Loader2,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { StaggerList, StaggerItem } from '../components/animations/StaggerList';
import { api } from '../lib/api';

interface DashboardStats {
  totalClients: number;
  activeProjects: number;
  pendingApprovals: number;
  totalComments: number;
}

interface Project {
  id: string;
  name: string;
  organizationName: string;
  pendingApprovalCount: number;
  commentCount: number;
  status: string;
  updatedAt: string;
}

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentProjects, setRecentProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchDashboardData() {
      try {
        setIsLoading(true);
        // API returns { data: [...], pagination: {...}, success: true }
        const [orgsRes, projectsRes] = await Promise.all([
          api.get<any[]>('/organizations'),
          api.get<Project[]>('/projects'),
        ]);

        // Safely extract data arrays with defensive checks
        const projects = Array.isArray(projectsRes.data) ? projectsRes.data : [];
        const orgs = Array.isArray(orgsRes.data) ? orgsRes.data : [];

        const pendingCount = projects.reduce((acc: number, p: Project) => acc + (p.pendingApprovalCount || 0), 0);

        setStats({
          totalClients: orgs.length,
          activeProjects: projects.filter((p: Project) => p.status === 'active').length,
          pendingApprovals: pendingCount,
          totalComments: projects.reduce((acc: number, p: Project) => acc + (p.commentCount || 0), 0),
        });

        setRecentProjects(projects.slice(0, 5));
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        // Set empty stats on error to prevent render issues
        setStats({
          totalClients: 0,
          activeProjects: 0,
          pendingApprovals: 0,
          totalComments: 0,
        });
      } finally {
        setIsLoading(false);
      }
    }
    fetchDashboardData();
  }, []);

  const statItems = stats ? [
    {
      label: 'Total Clients',
      value: stats.totalClients.toString(),
      icon: Users,
      color: 'from-blue-500 to-cyan-500',
    },
    {
      label: 'Active Projects',
      value: stats.activeProjects.toString(),
      icon: FolderKanban,
      color: 'from-accent to-purple-500',
    },
    {
      label: 'Pending Approvals',
      value: stats.pendingApprovals.toString(),
      icon: CheckCircle2,
      color: 'from-amber-500 to-orange-500',
    },
    {
      label: 'Total Comments',
      value: stats.totalComments.toString(),
      icon: MessageSquare,
      color: 'from-green-500 to-emerald-500',
    },
  ] : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-text-secondary mt-1">
          Welcome back! Here's what's happening with your projects.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <StaggerList className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statItems.map((stat) => (
          <StaggerItem key={stat.label}>
            <Card className="relative overflow-hidden" glow>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-text-secondary">{stat.label}</p>
                    <p className="text-3xl font-bold mt-1">{stat.value}</p>
                  </div>
                  <div
                    className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}
                  >
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
                {/* Decorative gradient */}
                <div
                  className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${stat.color} opacity-50`}
                />
              </CardContent>
            </Card>
          </StaggerItem>
        ))}
      </StaggerList>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Projects */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-2"
        >
          <Card hover={false}>
            <div className="p-6 border-b border-border">
              <h2 className="text-lg font-semibold">Recent Projects</h2>
            </div>
            <CardContent className="p-0">
              {recentProjects.length === 0 ? (
                <div className="p-8 text-center text-text-muted">
                  <FolderKanban className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No projects yet</p>
                  <Link to="/projects/new" className="text-accent hover:underline mt-2 inline-block">
                    Create your first project
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {recentProjects.map((project, index) => (
                    <Link key={project.id} to={`/projects/${project.id}`}>
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + index * 0.1 }}
                        className="p-4 hover:bg-bg-tertiary/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-accent-light flex items-center justify-center text-white font-semibold">
                            <FolderKanban className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{project.name}</p>
                            <p className="text-sm text-text-muted">{project.organizationName}</p>
                          </div>
                          {project.pendingApprovalCount > 0 && (
                            <span className="px-2 py-1 text-xs bg-amber-500/10 text-amber-500 rounded-full">
                              {project.pendingApprovalCount} pending
                            </span>
                          )}
                          <ArrowUpRight className="w-4 h-4 text-text-muted" />
                        </div>
                      </motion.div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card hover={false}>
            <div className="p-6 border-b border-border">
              <h2 className="text-lg font-semibold">Quick Actions</h2>
            </div>
            <CardContent className="space-y-3">
              <Link to="/clients/new">
                <motion.div
                  whileHover={{ scale: 1.02, x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  className="p-4 rounded-xl bg-bg-tertiary hover:bg-accent/10 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium">New Client</p>
                      <p className="text-sm text-text-muted">Add a new client organization</p>
                    </div>
                  </div>
                </motion.div>
              </Link>

              <Link to="/projects/new">
                <motion.div
                  whileHover={{ scale: 1.02, x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  className="p-4 rounded-xl bg-bg-tertiary hover:bg-accent/10 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-accent/10 text-accent group-hover:bg-accent group-hover:text-white transition-colors">
                      <FolderKanban className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium">New Project</p>
                      <p className="text-sm text-text-muted">Start a new client project</p>
                    </div>
                  </div>
                </motion.div>
              </Link>

              <Link to="/projects">
                <motion.div
                  whileHover={{ scale: 1.02, x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  className="p-4 rounded-xl bg-bg-tertiary hover:bg-accent/10 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/10 text-green-500 group-hover:bg-green-500 group-hover:text-white transition-colors">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium">Review Pending</p>
                      <p className="text-sm text-text-muted">{stats?.pendingApprovals || 0} items awaiting approval</p>
                    </div>
                  </div>
                </motion.div>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
